<template>
	<div class="quotation-summary-container">
		<div class="quotation-header">
			<div class="container">
				<div class="header-content">
					<div class="header-title-section">
						<h1 class="page-title">Quotation Summary</h1>
						<p class="page-subtitle">
							A summary of your selected quotation details
						</p>
					</div>
				</div>
			</div>
		</div>

		<div class="main-content">
			<div class="container">
				<div class="content-grid">
					<div class="summary-card">
						<div class="card-content">
							<h2 class="section-title">Summary</h2>
							<div
								class="border-2 border-gray-300 p-4 mb-4 rounded text-center bg-gray-50"
							>
								<p class="text-sm py-2 leading-relaxed">
									You are buying
									<span class="font-semibold">{{
										selectedClientQuoteSummary
											.product_details[0].product_name
									}}</span>
									<br />from
									<span class="font-semibold">{{
										selectedClientQuoteSummary
											.intermediary_details[0].kyc[0]
											.intermediary_names
									}}</span>
								</p>
							</div>
							<ul class="space-y-4">
								<li class="detail-item">
									<div class="detail-label">
										<i
											class="fas fa-money-bill-wave detail-icon"
										></i>
										Premium Amount
									</div>
									<div class="detail-value">
										{{
											selectedClientQuoteSummary.totalPremiumAmount.toFixed(
												2,
											)
										}}
										Ksh
									</div>
								</li>
								<li class="detail-item">
									<div class="detail-label">
										<i
											class="fas fa-plus-circle detail-icon"
										></i>
										Additional Premium
									</div>
									<div class="detail-value">
										{{
											selectedClientQuoteSummary.risks[0].totalAdditionalPremium.toFixed(
												2,
											)
										}}
										Ksh
									</div>
								</li>
								<li class="detail-item">
									<div class="detail-label">
										<i
											class="fas fa-file-invoice-dollar detail-icon"
										></i>
										Charges
									</div>
									<div class="detail-value">
										{{
											selectedClientQuoteSummary.totalCharges.toFixed(
												2,
											)
										}}
										Ksh
									</div>
								</li>
							</ul>
							<div
								class="mt-4 border-t-2 border-gray-300 pt-2"
							></div>
							<div class="navigation-actions">
								<button
									@click="proceedToPayment"
									:disabled="isAnimating"
									class="btn btn-primary next-btn"
									:class="{ 'is-animating': isAnimating }"
								>
									<span v-if="!isAnimating">
										Proceed to Payment
										<i class="fas fa-arrow-right ms-2"></i>
									</span>
									<span v-else>
										Redirecting
										<i
											class="fas fa-spinner fa-spin ms-2"
										></i>
									</span>
								</button>
							</div>
						</div>
					</div>
					<div class="right-column-wrapper"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
	name: 'QuotationSummary',
	computed: {
		...mapGetters('clientQuotationDetails', [
			'premiumCalculations',
			'selectedBenefitsData',
			'selectedClientRecommendation',
		]),

		selectedClientQuoteSummary() {
			if (
				!this.selectedClientRecommendation ||
				!this.premiumCalculations
			) {
				// Return a default structure instead of null
				return {
					product_details: [{ product_name: 'Loading...' }],
					intermediary_details: [
						{ kyc: [{ intermediary_names: 'Loading...' }] },
					],
					totalPremiumAmount: 0,
					risks: [{ totalAdditionalPremium: 0 }],
					totalCharges: 0,
				};
			}
			return {
				product_details: [
					{
						product_name:
							this.selectedClientRecommendation.product.name,
					},
				],
				intermediary_details: [
					{
						kyc: [
							{
								intermediary_names:
									this.selectedClientRecommendation
										.intermediary.kyc[0].intermediary_names,
							},
						],
					},
				],
				totalPremiumAmount: this.premiumCalculations.totalPremium,
				risks: [
					{
						totalAdditionalPremium:
							this.premiumCalculations.additionalPremium,
					},
				],
				totalCharges: this.premiumCalculations.totalCharges || 0,
			};
		},
	},
	methods: {
		...mapActions('clientQuotationDetails', [
			'viewClientRecommendation',
			'acceptClientRecommendation',
		]),

		formatCurrency(value, currency) {
			if (typeof value !== 'number') return 'N/A';
			const formatter = new Intl.NumberFormat('en-KE', {
				style: 'currency',
				currency: currency,
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return formatter.format(value);
		},

		async proceedToPayment() {
			this.$router.push({
				name: 'QuotationPayment',
				params: { id: this.$route.params.id },
			});
		},

		async acceptQuotation() {
			if (!this.selectedClientRecommendation) return;
			try {
				await this.acceptClientRecommendation(
					this.selectedClientRecommendation,
				);
				this.$notify({
					type: 'success',
					title: 'Success',
					text: 'Quotation accepted successfully. You can now proceed to payment.',
					duration: 5000,
				});
				Event.$emit('notifications:show', {
					type: 'Success',
					title: 'Success',
					message:
						'Quotation accepted successfully. You can now proceed to payment.',
					duration: 3000,
				});
			} catch (error) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Acceptance Error',
					message: 'Failed to accept quotation.',
					duration: 3000,
				});
			}
		},
	},
	mounted() {
		const ref = this.$route.params.id;
		if (ref && !this.selectedClientRecommendation) {
			this.viewClientRecommendation(ref);
		}
	},
};
</script>

<style lang="less" scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css');

@primary-color: #2563eb;
@primary-hover: #1d4ed8;
@light-bg: #f8fafc;
@white: #ffffff;
@gray-50: #f9fafb;
@gray-100: #f3f4f6;
@gray-200: #e5e7eb;
@gray-300: #d1d5db;
@gray-600: #4b5563;
@gray-900: #111827;
@success-color: #10b981;

.quotation-summary-container {
	min-height: 100vh;
	background: linear-gradient(135deg, @light-bg 0%, #e2e8f0 100%);
	font-family:
		'Inter',
		-apple-system,
		BlinkMacSystemFont,
		'Segoe UI',
		sans-serif;
}

.quotation-header {
	background: @white;
	border-bottom: 1px solid @gray-200;
	padding: 2rem 0 1.5rem;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

	.header-content {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.header-title-section {
		.page-title {
			font-size: 2rem;
			font-weight: 700;
			color: @gray-900;
			margin: 0 0 0.5rem 0;
			line-height: 1.2;
		}

		.page-subtitle {
			font-size: 1.125rem;
			color: @gray-600;
			margin: 0;
			font-weight: 400;
		}
	}
}

.main-content {
	padding: 2rem 0;
	display: flex;
	justify-content: center;

	.container {
		max-width: 800px;
		width: 100%;
	}

	.content-grid {
		display: flex;
		justify-content: center;
		gap: 2rem;
	}
}

.summary-card {
	background: @white;
	border-radius: 16px;
	box-shadow:
		0 4px 6px rgba(0, 0, 0, 0.05),
		0 1px 3px rgba(0, 0, 0, 0.1);
	overflow: hidden;
	width: 100%;

	.card-content {
		padding: 2rem;
	}

	.section-title {
		font-size: 1.5rem;
		font-weight: 700;
		color: @gray-900;
		margin: 0 0 1.5rem 0;
	}

	.detail-item {
		display: flex;
		justify-content: space-between;
		align-items: center;
		.detail-label {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			font-size: 0.875rem;
			font-weight: 600;
			color: @gray-600;

			.detail-icon {
				color: @primary-color;
				font-size: 0.875rem;
			}
		}

		.detail-value {
			font-size: 1rem;
			color: @gray-900;
			font-weight: 500;
		}
	}

	.navigation-actions {
		display: flex;
		justify-content: flex-end;
		width: 100%;
		margin-top: 2rem;
	}

	.next-btn {
		position: relative;
		overflow: hidden;
		width: 100%;
		padding: 0.75rem 1.5rem;
		background: @primary-color;
		border: 2px solid @primary-color;
		color: @white;
		font-weight: 600;
		border-radius: 8px;
		transition: all 0.2s ease;
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 1;

		&:hover {
			background: @primary-hover;
			border-color: @primary-hover;
			transform: translateY(-1px);
			box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
		}

		&.is-animating {
			pointer-events: none;
			background-color: @success-color;
			border-color: @success-color;

			span {
				opacity: 0;
				transition: opacity 0.5s ease-in-out;
			}
		}

		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: @primary-color;
			transform: translateX(-100%);
			transition: transform 0.5s ease-in-out;
			z-index: -1;
		}

		&.is-animating::after {
			transform: translateX(0);
		}
	}
}
</style>
